import React, { useRef, useState } from "react";
import { api, formatError } from "@/lib/api";
import { toast } from "@/lib/toast";
import { ImagePlus, Film, X, Loader2 } from "lucide-react";

// Read a video's duration in-browser so we can reject >60s / >50MB BEFORE uploading.
function checkVideo(file) {
  return new Promise((resolve) => {
    if (file.size > 50 * 1024 * 1024) return resolve("Video is over 50MB.");
    try {
      const url = URL.createObjectURL(file);
      const v = document.createElement("video");
      v.preload = "metadata";
      v.onloadedmetadata = () => { URL.revokeObjectURL(url); resolve(v.duration > 61 ? "Video is longer than 60 seconds." : null); };
      v.onerror = () => { URL.revokeObjectURL(url); resolve(null); }; // unreadable here -> let the server decide
      v.src = url;
    } catch { resolve(null); }
  });
}

// Shared photo(<=4) + video(1, <=60s/50MB) attachment control for the thread composer AND the reply
// box. Controlled: parent owns images[]/video state so the payload stays a single source of truth.
export function ForumMediaUploader({ P, images, setImages, video, setVideo, compact = false }) {
  const photoRef = useRef(null);
  const videoRef = useRef(null);
  const [busy, setBusy] = useState("");

  const onPhotos = async (e) => {
    const files = Array.from(e.target.files || []); e.target.value = "";
    if (!files.length) return;
    const room = 4 - images.length;
    if (room <= 0) { toast.error("You can attach up to 4 photos."); return; }
    setBusy("photo");
    try {
      for (const f of files.slice(0, room)) {
        if (f.size > 10 * 1024 * 1024) { toast.error(`${f.name} is over 10MB.`); continue; }
        const fd = new FormData(); fd.append("file", f);
        const { data } = await api.post("/forum/upload-photo", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setImages((prev) => (prev.length < 4 ? [...prev, data.url] : prev));
      }
    } catch (e2) { toast.error(formatError(e2.response?.data?.detail) || "Photo upload failed."); }
    finally { setBusy(""); }
  };

  const onVideo = async (e) => {
    const f = (e.target.files || [])[0]; e.target.value = "";
    if (!f) return;
    const problem = await checkVideo(f);
    if (problem) { toast.error(problem); return; }
    setBusy("video");
    try {
      const fd = new FormData(); fd.append("file", f);
      const { data } = await api.post("/forum/upload-video", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setVideo(data.url);
    } catch (e2) { toast.error(formatError(e2.response?.data?.detail) || "Video upload failed."); }
    finally { setBusy(""); }
  };

  const box = compact ? "w-14 h-14" : "w-16 h-16";
  return (
    <div data-testid="forum-media-uploader">
      <div className="flex flex-wrap gap-2" data-testid="forum-media-thumbs">
        {images.map((url) => (
          <div key={url} className={`relative ${box} border`} style={{ borderColor: P.cardBorder }}>
            <img src={url} alt="" className="w-full h-full object-cover" />
            <button type="button" onClick={() => setImages((prev) => prev.filter((u) => u !== url))} data-testid="forum-media-photo-remove"
              className="absolute -top-2 -right-2 w-5 h-5 flex items-center justify-center bg-black border text-white" style={{ borderColor: P.cardBorder }}>
              <X className="w-3 h-3" />
            </button>
          </div>
        ))}
        {images.length < 4 && (
          <button type="button" onClick={() => photoRef.current?.click()} disabled={busy === "photo"} data-testid="forum-media-add-photo"
            className={`${box} border flex items-center justify-center transition-colors disabled:opacity-50 hover:border-olive`}
            style={{ borderColor: P.cardBorder, color: P.textMuted }} title="Add photos (up to 4)">
            {busy === "photo" ? <Loader2 className="w-5 h-5 animate-spin" /> : <ImagePlus className="w-5 h-5" />}
          </button>
        )}
        {video ? (
          <div className={`relative ${box} border overflow-hidden`} style={{ borderColor: P.cardBorder }} data-testid="forum-media-video-chip">
            <video src={video} className="w-full h-full object-cover" muted />
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none"><Film className="w-4 h-4 text-white/90" /></div>
            <button type="button" onClick={() => setVideo(null)} data-testid="forum-media-video-remove"
              className="absolute -top-2 -right-2 w-5 h-5 flex items-center justify-center bg-black border text-white" style={{ borderColor: P.cardBorder }}>
              <X className="w-3 h-3" />
            </button>
          </div>
        ) : (
          <button type="button" onClick={() => videoRef.current?.click()} disabled={busy === "video"} data-testid="forum-media-add-video"
            className={`${box} border flex items-center justify-center transition-colors disabled:opacity-50 hover:border-olive`}
            style={{ borderColor: P.cardBorder, color: P.textMuted }} title="Add one video (max 60s / 50MB)">
            {busy === "video" ? <Loader2 className="w-5 h-5 animate-spin" /> : <Film className="w-5 h-5" />}
          </button>
        )}
      </div>
      <input ref={photoRef} type="file" accept="image/*" multiple hidden onChange={onPhotos} data-testid="forum-media-photo-input" />
      <input ref={videoRef} type="file" accept="video/*" hidden onChange={onVideo} data-testid="forum-media-video-input" />
    </div>
  );
}
